import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    Button,
} from '@/shared/ui'
import { cn } from '@/true-fsd/shared/utils/css'
import type { MarkingEditorProps, ICreativeWithMarkableFile } from './types'
import { useMarkingEditor } from './hooks'
import { ConfirmCloseDialog } from './components/ConfirmCloseDialog'
import { MarkingEditorCanvas } from './MarkingEditorCanvas/MarkingEditorCanvas'
import { FontSizeSlider } from './components/FontSizeSlider'
import { TextEditor } from './components/TextEditor'
import { MarkingEditorToolbar } from './components/MarkingEditorToolbar'
import { getMediaUrlFromCreative } from './utils'
import { DialogDescription } from '@/shared/ui'
import { MARKING_DIALOG_LAYOUT } from '../shared/MarkingPartLayout/data'
import { CANVAS } from './constants'
import classes from './MarkingEditorModal.module.scss'

/**
 * Модальное окно редактора маркировки
 */
export function MarkingEditorModal({
    creative,
    initialData,
    onSave,
    onClose,
    open,
    onOpenChange,
    ...wwld
}: MarkingEditorProps & {
    /**
     * Открыто ли модальное окно
     */
    open: boolean

    /**
     * Обработчик изменения состояния открытия
     */
    onOpenChange: (open: boolean) => void
}) {
    // Используем композицию хуков для редактора
    const editor = useMarkingEditor({
        wwld,
        initialData,
        isOpen: open,
    })

    // Отслеживаем предыдущее состояние open для обновления состояния при открытии
    const prevOpenRef = useRef(open)
    const prevSavedDataRef = useRef(editor.savedData)
    const prevIsFetchingRef = useRef(editor.isFetching)
    const prevCreativeIdRef = useRef(creative?.id)
    const resetToCurrentDataRef = useRef(editor.resetToCurrentData)

    // Обновляем ref для resetToCurrentData при изменении
    resetToCurrentDataRef.current = editor.resetToCurrentData

    // Сбрасываем состояние при открытии модального окна, чтобы загрузить актуальные данные
    useEffect(() => {
        const creativeId = creative?.id
        const creativeChanged = prevCreativeIdRef.current !== creativeId

        // Если креатив изменился и редактор открыт, ждем завершения загрузки перед сбросом
        // Это предотвращает использование старых данных из кэша
        if (
            open &&
            creativeChanged &&
            prevCreativeIdRef.current !== undefined
        ) {
            // Не сбрасываем состояние, пока данные не загружены
            // Ждем завершения isFetching
            if (!editor.isFetching && editor.savedData !== undefined) {
                resetToCurrentDataRef.current()
            }
            // Обновляем refs и выходим, чтобы не выполнять другие проверки
            prevOpenRef.current = open
            prevSavedDataRef.current = editor.savedData
            prevIsFetchingRef.current = editor.isFetching
            prevCreativeIdRef.current = creativeId
            return
        }

        // Сбрасываем после завершения загрузки (isFetching было true, стало false)
        // Используем resetToCurrentData чтобы гарантированно получить актуальные данные
        if (
            open &&
            prevIsFetchingRef.current &&
            !editor.isFetching &&
            editor.savedData !== undefined
        ) {
            resetToCurrentDataRef.current()
        }

        // Также сбрасываем состояние, если savedData изменился (например, после сохранения)
        // НО только если креатив не изменился (это обрабатывается выше)
        if (
            open &&
            !editor.isFetching &&
            editor.savedData !== undefined &&
            prevSavedDataRef.current !== editor.savedData &&
            !creativeChanged
        ) {
            resetToCurrentDataRef.current()
        }

        // Если модальное окно только что открылось и данные уже загружены
        if (open && !prevOpenRef.current) {
            if (editor.savedData !== undefined && !editor.isFetching) {
                resetToCurrentDataRef.current()
            }
        }

        prevOpenRef.current = open
        prevSavedDataRef.current = editor.savedData
        prevIsFetchingRef.current = editor.isFetching
        prevCreativeIdRef.current = creativeId
        // Используем только нужные свойства вместо всего объекта editor
    }, [open, editor.savedData, editor.isFetching, creative?.id])

    // Состояние для диалога подтверждения закрытия
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)

    // Состояние для кастомных цветов (из savedData)
    // Преобразуем строку с разделителем запятой в массив
    const customTextColors = useMemo(() => {
        const colors = editor.savedData?.mark_media_custom_text_colors
        if (!colors || typeof colors !== 'string') return []
        // Разделяем строку по запятой и фильтруем пустые значения
        return colors.split(',').filter((c): c is string => c.trim().length > 0)
    }, [editor.savedData?.mark_media_custom_text_colors])

    const customBackgroundColors = useMemo(() => {
        const colors = editor.savedData?.mark_media_custom_background_colors
        if (!colors || typeof colors !== 'string') return []
        // Разделяем строку по запятой и фильтруем пустые значения
        return colors.split(',').filter((c): c is string => c.trim().length > 0)
    }, [editor.savedData?.mark_media_custom_background_colors])

    // Состояние для локальных изменений кастомных цветов
    const [localCustomTextColors, setLocalCustomTextColors] = useState<string[]>([])
    const [localCustomBackgroundColors, setLocalCustomBackgroundColors] =
        useState<string[]>([])

    // Синхронизируем локальное состояние с данными из БД
    useEffect(() => {
        setLocalCustomTextColors(customTextColors)
    }, [customTextColors])

    useEffect(() => {
        setLocalCustomBackgroundColors(customBackgroundColors)
    }, [customBackgroundColors])

    // Креатив уже имеет нужный тип

    /**
     * Обработчик закрытия модального окна
     */
    const handleClose = useCallback(() => {
        if (editor.hasChanges) {
            setShowConfirmDialog(true)
        } else {
            onOpenChange(false)
            onClose?.()
        }
    }, [editor.hasChanges, onOpenChange, onClose])

    /**
     * Обработчик подтверждения закрытия без сохранения
     */
    const handleConfirmClose = useCallback(() => {
        setShowConfirmDialog(false)
        onOpenChange(false)
        onClose?.()
    }, [onOpenChange, onClose])

    /**
     * Обработчик отмены закрытия (вернуться в редактор)
     */
    const handleCancelClose = useCallback(() => {
        setShowConfirmDialog(false)
    }, [])

    /**
     * Обработчик сохранения изменений
     */
    const handleSave = useCallback(() => {
        editor.save(localCustomTextColors, localCustomBackgroundColors)
        onSave?.(editor.state)
        onOpenChange(false)
        onClose?.()
    }, [
        editor,
        localCustomTextColors,
        localCustomBackgroundColors,
        onSave,
        onOpenChange,
        onClose,
    ])

    /**
     * Обработчик изменения состояния открытия Dialog
     */
    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (!newOpen) {
                handleClose()
            } else {
                onOpenChange(newOpen)
            }
        },
        [handleClose, onOpenChange]
    )

    // Определение состояния кнопки "Готово"
    const isSaveButtonDisabled = !editor.hasChanges
    const saveButtonClassName = useMemo(
        () =>
            cn(
                classes.saveButton,
                isSaveButtonDisabled
                    ? classes.saveButtonDisabled
                    : classes.saveButtonActive
            ),
        [isSaveButtonDisabled]
    )

    // Стили для overlay (затемнение только бокового меню-слайдера, как в EventsDialog)
    // Переопределяем inset: 0, чтобы ограничить overlay только правой частью экрана
    const OVERLAY_STYLE: React.CSSProperties = {
        right: 0,
        left: 'auto',
        width: MARKING_DIALOG_LAYOUT.totalWidth,
        top: 0,
        bottom: 0,
        // Убираем inset: 0, который задается по умолчанию
    }

    return (
        <>
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent
                    className={cn(classes.dialogContent)}
                    closeClassName={classes.closeButton}
                    overlayStyle={OVERLAY_STYLE}
                    overlayClassName={classes.overlay}
                    aria-describedby={undefined}
                >
                    <DialogHeader>
                        <DialogTitle className="sr-only">
                            Редактор маркировки
                        </DialogTitle>
                    </DialogHeader>
                    <div className={classes.content}>
                        <FontSizeSlider
                            value={editor.state.fontSize}
                            onChange={editor.setFontSize}
                            className={classes.fontSizeSlider}
                        />
                        <div className={classes.canvasWrapper}>
                            <MarkingEditorCanvas
                                imageUrl={getMediaUrlFromCreative(creative)}
                                width={CANVAS.MODAL_WIDTH}
                                height={CANVAS.MODAL_HEIGHT}
                                text={editor.state.markMediaText}
                                fontSize={editor.state.fontSize}
                                textColor={editor.state.markMediaColor}
                                textAlignment={editor.state.alignment}
                                fontFamily={editor.state.fontFamily}
                                onFontSizeChange={editor.setFontSize}
                                onTextChange={editor.setMarkMediaText}
                                position={editor.state.position}
                                onPositionChange={editor.setPosition}
                                hasBackground={editor.state.hasBackground}
                                backgroundColor={editor.state.backgroundColor}
                                onScaleChange={editor.handleScaleChange}
                                initialScale={editor.scale}
                                onError={(error) => {
                                    // Ошибка обрабатывается в компоненте MediaErrorPlaceholder
                                    // Не выбрасываем дальше, чтобы предотвратить краш
                                }}
                            />
                        </div>
                    </div>

                    <DialogFooter className={classes.footer}>
                        <div className={classes.footerLeft}>
                            <MarkingEditorToolbar
                                textColor={editor.state.markMediaColor}
                                onTextColorChange={editor.setMarkMediaColor}
                                alignment={editor.state.alignment}
                                onAlignmentChange={editor.setAlignment}
                                fontFamily={editor.state.fontFamily}
                                onFontFamilyChange={editor.setFontFamily}
                                hasBackground={editor.state.hasBackground}
                                onHasBackgroundChange={editor.setHasBackground}
                                backgroundColor={editor.state.backgroundColor}
                                onBackgroundColorChange={
                                    editor.setBackgroundColor
                                }
                                customTextColors={localCustomTextColors}
                                onCustomTextColorsChange={setLocalCustomTextColors}
                                customBackgroundColors={localCustomBackgroundColors}
                                onCustomBackgroundColorsChange={
                                    setLocalCustomBackgroundColors
                                }
                            />
                        </div>
                        <TextEditor
                            value={editor.state.markMediaText}
                            onChange={editor.setMarkMediaText}
                            className={classes.textEditor}
                            placeholder="Введите тест..."
                            hideLabel={true}
                        />
                        <div className={classes.footerRight}>
                            <Button
                                className={saveButtonClassName}
                                variant="primary"
                                disabled={isSaveButtonDisabled}
                                onClick={handleSave}
                            >
                                Готово
                            </Button>
                        </div>
                    </DialogFooter>

                    <div
                        className={cn(classes.confirmDialogWrapper, {
                            [classes.confirmDialogWrapperActive]:
                                showConfirmDialog,
                        })}
                    >
                        <ConfirmCloseDialog
                            open={showConfirmDialog}
                            onOpenChange={setShowConfirmDialog}
                            onConfirm={handleConfirmClose}
                            onCancel={handleCancelClose}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
